import { browser } from "$app/environment";
import { generateNickname, sanitizeHandle } from "$data/nicknames";

/**
 * VuTwin per-device profile.
 *
 * Persists the user-visible identity (handle) and lifetime stats (pulses
 * played, current streak, last play date) to localStorage. Mirrors the
 * pattern used by `identity.svelte.ts` and `pair.svelte.ts`.
 *
 * Why a separate store instead of game state:
 * - Survives page reload (game state is in-memory only).
 * - Read by Bond / Intro / Share without coupling them to the gameplay store.
 * - Treats the streak as a real lifetime counter (last-played-ymd based)
 *   rather than the magic `streak = 12` placeholder previously hardcoded
 *   in `game.svelte.ts`.
 */

const STORAGE_KEY = "vutwin.profile.v2";
const LEGACY_KEYS = ["vutwin.profile.v1"];

interface StoredProfile {
  handle: string;
  pulsesPlayed: number;
  streak: number;
  lastPlayedYmd: string;
}

/** UTC YYYY-MM-DD for streak bookkeeping (matches `crypto/seed.ts` ymd). */
function ymdUtc(d: Date = new Date()): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Days between two YMD strings (b - a). 0 if same day, 1 if a was yesterday, etc. */
function ymdDiff(a: string, b: string): number {
  const ta = Date.UTC(+a.slice(0, 4), +a.slice(5, 7) - 1, +a.slice(8, 10));
  const tb = Date.UTC(+b.slice(0, 4), +b.slice(5, 7) - 1, +b.slice(8, 10));
  return Math.round((tb - ta) / 86_400_000);
}

class ProfileState {
  handle = $state<string>("");
  pulsesPlayed = $state<number>(0);
  streak = $state<number>(0);
  lastPlayedYmd = $state<string>("");

  private hydrated = false;

  /** Load (or mint) the persistent profile. Idempotent. */
  hydrate(): void {
    if (this.hydrated) return;
    this.hydrated = true;
    if (!browser) return;

    for (const k of LEGACY_KEYS) localStorage.removeItem(k);

    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const data = JSON.parse(raw) as Partial<StoredProfile>;
        this.handle = sanitizeHandle(data.handle ?? "") || generateNickname();
        this.pulsesPlayed = Number(data.pulsesPlayed) || 0;
        this.streak = Number(data.streak) || 0;
        this.lastPlayedYmd =
          typeof data.lastPlayedYmd === "string" ? data.lastPlayedYmd : "";
        if (!data.handle) this.persist(); // mint + persist generated handle
        return;
      } catch {
        // corrupt storage — fall through and re-mint
      }
    }

    this.handle = generateNickname();
    this.pulsesPlayed = 0;
    this.streak = 0;
    this.lastPlayedYmd = "";
    this.persist();
  }

  /** Sanitize + persist a user-edited handle. Empty input mints a new one. */
  setHandle(value: string): string {
    const cleaned = sanitizeHandle(value || "");
    this.handle = cleaned || generateNickname();
    this.persist();
    return this.handle;
  }

  /** Record a played Pulse. Updates streak based on UTC day boundaries. */
  recordPulse(): void {
    const today = ymdUtc();
    if (this.lastPlayedYmd === today) {
      // Already played today — count the pulse but don't double-streak.
      this.pulsesPlayed += 1;
    } else {
      const diff = this.lastPlayedYmd
        ? ymdDiff(this.lastPlayedYmd, today)
        : Infinity;
      if (diff === 1)
        this.streak += 1; // played yesterday → continues
      else this.streak = 1; // first play or streak broken
      this.pulsesPlayed += 1;
      this.lastPlayedYmd = today;
    }
    this.persist();
  }

  /** Wipe the profile (debug / "start over"). */
  reset(): void {
    if (browser) localStorage.removeItem(STORAGE_KEY);
    this.hydrated = false;
    this.handle = "";
    this.pulsesPlayed = 0;
    this.streak = 0;
    this.lastPlayedYmd = "";
  }

  private persist(): void {
    if (!browser) return;
    const payload: StoredProfile = {
      handle: this.handle,
      pulsesPlayed: this.pulsesPlayed,
      streak: this.streak,
      lastPlayedYmd: this.lastPlayedYmd,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // localStorage full / disabled — silent
    }
  }
}

export const profile = new ProfileState();
